import React from 'react';
import { ConfigProvider } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../theme/ThemeContext';
import { fixUnclosedColorSpans, agenticMarkdownComponents } from '../../shared/markdownComponents';

export type FindingTextType = 'regular' | 'bold' | 'label' | 'positive' | 'negative' | 'orange';

export interface FindingTextSegment {
    /** The text content */
    text: string;
    /** Type of text styling */
    type?: FindingTextType;
}

export interface FindingItem {
    /** Array of text segments with formatting */
    segments: FindingTextSegment[];
}

export interface KeyFindingsProps {
    /** Title of the findings section */
    title?: string;
    /** Array of finding items */
    items?: FindingItem[];
    /** When true, render item text as markdown (tables, headers, lists, etc.) - like AnalysisSteps */
    renderAsMarkdown?: boolean;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

const KeyFindings: React.FC<KeyFindingsProps> = (props) => {
    const { items = [], renderAsMarkdown = false } = props;

    const theme = useTheme();

    const getTextStyle = (type: FindingTextType = 'regular'): string => {
        switch (type) {
            case 'label':
                return 'text-[16px] leading-[24px] text-[#1f1f1f]';
            case 'bold':
                return 'text-[16px] leading-[24px] text-[#140b1d]';
            case 'positive':
                return 'text-[16px] leading-[24px] text-[#00a63e]';
            case 'negative':
                return 'text-[16px] leading-[24px] text-[#ec343e]';
            case 'orange':
                return 'text-[16px] leading-[24px] text-[#ff6b35]';
            case 'regular':
                return 'text-[16px] leading-[24px]';
            default:
                return 'text-[16px] leading-[24px] font-normal text-[#4a5565]';
        }
    };

    const renderFindingText = (segments: FindingTextSegment[]) => {
        return segments.map((segment, index) => (
            <span key={index} className={getTextStyle(segment.type)}>
                {segment.text}
            </span>
        ));
    };

    const renderItemContent = (item: FindingItem) => {
        const text = fixUnclosedColorSpans(item.segments.map((s) => s.text).join(''));
        if (renderAsMarkdown && text) {
            return (
                <div className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] markdown-content">
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        remarkRehypeOptions={{ allowDangerousHtml: true }}
                        components={agenticMarkdownComponents}
                    >
                        {text}
                    </ReactMarkdown>
                </div>
            );
        }
        return renderFindingText(item.segments);
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex flex-col gap-2 items-start px-3 py-4 rounded-lg w-full border border-[#faecd3]"
                style={{
                    backgroundImage: 'linear-gradient(-25deg, rgb(255, 253, 249) 10%, rgb(253, 248, 238) 96%)',
                }}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-key-findings
            >
                {/* Title */}
                {/* <p className="font-sans font-semibold text-[16px] leading-[22px] text-[#1f1f1f] m-0">{title}</p> */}

                {/* Finding Items */}
                <div className="flex items-start w-full">
                    {renderAsMarkdown ? (
                        <ul className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] w-full m-0 pl-5 list-disc space-y-2">
                            {items.map((item, index) => (
                                <li key={index} className="mb-0">
                                    {renderItemContent(item)}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="font-sans text-[16px] leading-[24px] m-0 pl-5 list-disc w-full">
                            {items.map((item, index) => (
                                <li key={index} className="mb-0">
                                    {renderFindingText(item.segments)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default KeyFindings;
